/**
 * FineIndex Type Definitions
 * 
 * Asset database types for persistent GUID tracking,
 * importer settings, and fast indexed queries.
 * 
 * NO hardcoded content types or importers - all registered externally.
 * 
 * @module @engine/aid/types
 */

// ============================================
// ASSET ENTRY
// ============================================

/**
 * A produced/compiled asset generated from a source asset.
 * Stored in /.cache/ directory.
 */
export interface ProducedAsset {
  /** Type of produced asset (e.g. 'texture', 'script', 'atlas', 'vfx', 'pointmap') */
  type: string;
  
  /** Path in VFS (typically /.cache/...) */
  path: string;
  
  /** Output format (png, webp, wasm, etc.) */
  format?: string;
  
  /** File size in bytes */
  size?: number;
  
  /** Content hash for cache validation */
  hash?: string;
  
  /** When this produced asset was generated */
  producedAt: Date;
  
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Asset entry stored in the index database.
 *
 * The database is a rebuildable CACHE. The git source of truth for identity
 * (guid, importerType, importerParams, labels) is the asset's TOML `.meta`
 * sidecar (see MetaFile.ts); everything else here is derived import state.
 * Deleting the DB is always safe — refresh re-adopts identities from sidecars.
 */
export interface AssetEntry {
  /** Persistent unique identifier (stays same even if file renamed/moved) */
  guid: string;
  
  /** Current path in VFS */
  path: string;
  
  /** Content type identifier (registered externally) */
  contentType: string;
  
  /** Importer type identifier (registered externally) */
  importerType: string;
  
  /** Importer-specific parameters (opaque to FineIndex) */
  importerParams: Record<string, unknown>;
  
  /** File size in bytes */
  size: number;
  
  /** Content hash for change detection */
  contentHash: string;
  
  /** When asset was first indexed */
  createdAt: Date;
  
  /** When asset metadata was last updated */
  updatedAt: Date;
  
  /** When file was last modified (from VFS) */
  fileModifiedAt: Date;
  
  /** User-defined labels for organization */
  labels: string[];
  
  /** Import status */
  importStatus: 'pending' | 'imported' | 'failed';
  
  /** Last import error (if failed) */
  importError?: string;
  
  /** Artifacts produced by importer */
  artifacts?: ImportArtifact[];
  
  /** Dependencies on other assets (GUIDs) */
  dependencies?: string[];
  
  /** Custom data from importer */
  importedData?: Record<string, unknown>;
  
  /** Produced/compiled assets generated from this source (legacy - use sourceGuid instead) */
  producedAssets?: ProducedAsset[];
  
  /** GUID of source asset if this is a produced/derived asset */
  sourceGuid?: string;
}

// ============================================
// CONTENT TYPE & IMPORTER REGISTRATION
// ============================================

/**
 * Content type definition for registration
 */
export interface ContentTypeDefinition {
  /** Unique identifier for this content type */
  id: string;
  
  /** Display name */
  name: string;
  
  /** File extensions that match this type (without dot) */
  extensions: string[];

  /** Default importer type for this content */
  defaultImporter: string;
}

// ============================================
// IMPORTER SYSTEM
// ============================================

/**
 * Context passed to importer callback
 */
export interface ImportContext {
  /** Asset path in VFS */
  path: string;
  
  /** Asset GUID */
  guid: string;
  
  /** Detected content type */
  contentType: string;
  
  /** Raw file content */
  content: string | ArrayBuffer;
  
  /** MIME type of the file */
  mimeType?: string;
  
  /** File size */
  size: number;
  
  /** Importer parameters (user-configurable) */
  params: Record<string, unknown>;
  
  /** Read another file from VFS (for dependencies) */
  readFile: (path: string) => Promise<{ content: string | ArrayBuffer; size: number } | null>;
  
  /** Write a file to VFS cache */
  writeFile: (path: string, content: string | ArrayBuffer) => Promise<void>;
  
  /** Get GUID for another asset (creates if needed) */
  getGUID: (path: string) => Promise<string>;
  
  /** Register a produced asset (call after writing cache files) */
  addProducedAsset: (produced: Omit<ProducedAsset, 'producedAt'>) => void;
}

/**
 * Result returned by importer
 */
export interface ImportResult {
  /** Whether import was successful */
  success: boolean;
  
  /** Error message if failed */
  error?: string;
  
  /** Imported artifacts (e.g., sprite frames from atlas) */
  artifacts?: ImportArtifact[];
  
  /** Dependencies on other assets (by GUID) */
  dependencies?: string[];
  
  /** Custom metadata from importer */
  importedData?: Record<string, unknown>;
  
  /** Produced/compiled assets generated during import */
  producedAssets?: ProducedAsset[];
}

/**
 * An artifact produced by import (sub-asset)
 */
export interface ImportArtifact {
  /** Artifact identifier (unique within asset) */
  id: string;
  
  /** Display name */
  name: string;
  
  /** Artifact type (e.g., 'sprite', 'animation', 'clip') */
  type: string;
  
  /** Artifact-specific data */
  data: Record<string, unknown>;
}

/**
 * Importer callback function type
 */
export type ImporterCallback = (context: ImportContext) => Promise<ImportResult>;

/**
 * Importer definition for registration
 */
export interface ImporterDefinition {
  /** Unique identifier for this importer */
  id: string;
  
  /** Display name */
  name: string;
  
  /** Content types this importer can handle */
  supportedContentTypes: string[];
  
  /** Default parameters for new assets */
  defaultParams: Record<string, unknown>;

  /** Optional schema for params validation (JSON Schema subset) */
  paramsSchema?: ImporterParamsSchema;

  /** Import callback - called when asset needs to be imported */
  import?: ImporterCallback;

  /**
   * Content types this importer reads the COMPILED output of at import time (e.g. the atlas importer
   * reads compiled textures → `dependsOn: ['image']`). Drives import ORDER: dependencies are imported
   * first, so a full reindex never compiles a dependent before its inputs. The default order is computed
   * topologically from these declarations (no central hardcoded tier list); users can override it in
   * Project Settings. Omit for self-contained importers.
   */
  dependsOn?: string[];
}

/**
 * Simple schema for importer parameters
 */
export interface ImporterParamsSchema {
  type: 'object';
  properties: Record<string, {
    type: 'string' | 'number' | 'boolean' | 'array' | 'object';
    description?: string;
    default?: unknown;
    enum?: unknown[];
  }>;
  required?: string[];
}

// ============================================
// QUERY TYPES
// ============================================

/**
 * Query options for filtering assets
 */
export interface AssetQuery {
  /** Filter by content type(s) */
  contentType?: string | string[];
  
  /** Filter by importer type(s) */
  importerType?: string | string[];
  
  /** Filter by path prefix (folder) */
  pathPrefix?: string;
  
  /** Filter by labels (any match) */
  labels?: string[];
  
  /** Sort field */
  sortBy?: 'path' | 'contentType' | 'updatedAt' | 'size';
  
  /** Sort direction */
  sortOrder?: 'asc' | 'desc';
  
  /** Max results */
  limit?: number;
  
  /** Offset for pagination */
  offset?: number;
}

/**
 * Result of a query
 */
export interface AssetQueryResult {
  /** Matching entries */
  entries: AssetEntry[];
  
  /** Total count (ignoring limit/offset) */
  totalCount: number;
}

// ============================================
// INDEX STATISTICS
// ============================================

/**
 * Statistics about the asset index
 */
export interface IndexStats {
  /** Total number of indexed assets */
  totalAssets: number;
  
  /** Count by content type */
  byContentType: Record<string, number>;
  
  /** Count by importer type */
  byImporterType: Record<string, number>;
  
  /** Total size of all indexed assets */
  totalSize: number;
  
  /** When index was last refreshed */
  lastRefresh: Date | null;
}

// ============================================
// CHANGE TRACKING
// ============================================

/**
 * Types of changes detected during refresh
 */
export type ChangeType = 'added' | 'modified' | 'deleted' | 'moved';

/**
 * A detected change in the asset index
 */
export interface AssetChange {
  type: ChangeType;
  guid: string;
  path: string;
  previousPath?: string; // For 'moved' changes
}

/**
 * Result of a refresh operation
 */
export interface RefreshResult {
  changes: AssetChange[];
  stats: IndexStats;
  duration: number;
}

// ============================================
// PROGRESS CALLBACK
// ============================================

/**
 * Progress information for long operations
 */
export interface ProgressInfo {
  /** Current step/file being processed */
  current: number;
  
  /** Total steps/files to process */
  total: number;
  
  /** Current item path/name */
  currentItem?: string;
  
  /** Operation phase */
  phase: 'scanning' | 'indexing' | 'comparing' | 'updating' | 'importing' | 'rebuilding' | 'complete';
  
  /** Optional message */
  message?: string;
}

/**
 * Progress callback type
 */
export type ProgressCallback = (progress: ProgressInfo) => void;

// ============================================
// HELPERS
// ============================================

/**
 * Generate a new GUID (uses crypto.randomUUID if available)
 */
export function generateGUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  throw new Error('crypto.randomUUID is not available in this environment');
}

/**
 * Deterministic guid for a PRODUCED asset (v5-style: name-hashed, not random).
 * Same source + contentType (+ discriminator) always yields the same guid, so
 * reimports and `.cache` wipes never re-mint produced identities — anything that
 * ends up referencing a produced guid survives, because the parent's guid comes
 * from its committed `.meta` sidecar.
 *
 * FNV-1a over the key, four 32-bit rounds with different seeds, formatted as a
 * UUID with the version nibble set to 5 (marks it name-derived on inspection).
 */
export function deriveProducedGuid(sourceGuid: string, contentType: string, discriminator = ''): string {
  const key = `${sourceGuid} ${contentType} ${discriminator}`;
  const seeds = [0x811c9dc5, 0xcbf29ce4, 0x9e3779b9, 0x85ebca6b];
  const parts: string[] = [];
  for (const seed of seeds) {
    let h = seed >>> 0;
    for (let i = 0; i < key.length; i++) {
      h ^= key.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    parts.push(h.toString(16).padStart(8, '0'));
  }
  const hex = parts.join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-5${hex.slice(13, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

// ============================================
// DATABASE PROVIDER INTERFACE
// ============================================

/**
 * Abstract interface for FineIndex database storage.
 * 
 * Implementations:
 * - `FineIndexDB` — Dexie/IndexedDB (browser)
 * - `FineIndexSQLite` — better-sqlite3 (Electron/Node)
 */
export interface IFineIndexDB {
  // Lifecycle
  init(): Promise<void>;
  close(): Promise<void>;
  isReady(): boolean;

  // Fast lookups (from cache)
  getGuidForPath(path: string): string | null;
  getPathForGuid(guid: string): string | null;
  hasPath(path: string): boolean;
  hasGuid(guid: string): boolean;
  getAllPaths(): string[];
  getAllGuids(): string[];

  // Single operations
  getByGuid(guid: string): Promise<AssetEntry | null>;
  getByPath(path: string): Promise<AssetEntry | null>;
  getBySourceGuid(sourceGuid: string): Promise<AssetEntry[]>;
  put(entry: AssetEntry): Promise<void>;
  deleteByGuid(guid: string): Promise<boolean>;
  deleteByPath(path: string): Promise<boolean>;
  deleteAllProducedEntries(): Promise<number>;

  // Bulk operations
  bulkPut(entries: AssetEntry[], onProgress?: ProgressCallback): Promise<void>;
  bulkDelete(guids: string[], onProgress?: ProgressCallback): Promise<void>;

  // Queries
  getByContentType(contentType: string): Promise<AssetEntry[]>;
  getByImporterType(importerType: string): Promise<AssetEntry[]>;
  query(query: AssetQuery): Promise<AssetQueryResult>;

  // Statistics
  count(): Promise<number>;
  getStats(): Promise<IndexStats>;

  // Meta
  getMeta(key: string): Promise<string | null>;
  setMeta(key: string, value: string): Promise<void>;
  setLastRefresh(date: Date): Promise<void>;

  // Clear
  clear(): Promise<void>;
}

/**
 * Simple hash function for content (FNV-1a)
 */
export function hashContent(content: string | ArrayBuffer): string {
  let data: Uint8Array;
  
  if (typeof content === 'string') {
    data = new TextEncoder().encode(content);
  } else {
    data = new Uint8Array(content);
  }
  
  let hash = 2166136261; // FNV offset basis
  for (let i = 0; i < data.length; i++) {
    hash ^= data[i];
    hash = Math.imul(hash, 16777619); // FNV prime
  }
  
  // Convert to hex string
  return (hash >>> 0).toString(16).padStart(8, '0');
}
